import { Uppy } from '@uppy/core';
import ImageEditor from '@uppy/image-editor';
import EnglishLocale from '@uppy/locales/lib/en_US';
import { Dashboard, useUppy } from '@uppy/react/lib';
import ScreenCapture from '@uppy/screen-capture';
import Webcam from '@uppy/webcam';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import FrenchLocale from './fr_FR';
import i18n from 'FRONTEND/utils/i18n';
import { updateUppyState } from 'LEGACY/filesUpload';

import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import '@uppy/image-editor/dist/style.css';
import '@uppy/screen-capture/dist/style.css';
import '@uppy/webcam/dist/style.css';

const FileUploadField = ({
  allowedFileTypes,
  hideDescription,
  hideType,
  inputName,
  maxNumberOfFiles,
}) => {
  const locale = useSelector((state) => state.user.locale);
  const uppyLocale = locale === 'fr' ? FrenchLocale : EnglishLocale;

  const uppy = useUppy(() => {
    return new Uppy({
      id: inputName,
      restrictions: {
        allowedFileTypes,
        maxFileSize: 200 * 1024 * 1024, // 200Mb
        maxNumberOfFiles: maxNumberOfFiles || 10,
      },
      locale: uppyLocale,
    })
      .use(ImageEditor, { locale: uppyLocale })
      .use(ScreenCapture, { locale: uppyLocale, preferredVideoMimeType: 'video/webm' })
      .use(Webcam, {
        locale: uppyLocale,
        preferredImageMimeType: 'image/png',
        preferredVideoMimeType: 'video/webm',
        showVideoSourceDropdown: true,
      });
  });

  useEffect(() => {
    updateUppyState({ inputName, files: uppy.store.state.files });

    uppy.on('file-added', async(fileObject) => {
      updateUppyState({ inputName, files: uppy.store.state.files });
      // Specific logic for patient consent form.
      if (fileObject.source === 'patient-consent_form') {
        document.getElementById('patient-consent_1').checked = true;
      }
    });

    uppy.on('file-removed', async() => {
      updateUppyState({ inputName, files: uppy.store.state.files });
    });

    uppy.on('dashboard:file-edit-complete', async() => {
      updateUppyState({ inputName, files: uppy.store.state.files });
    });

    window.addEventListener('uppyRemoveFiles', (event) => {
      if (event.detail === inputName) {
        uppy.getFiles().forEach((file) => {
          uppy.removeFile(file.id);
        });
      }
    });

    return () => {
      updateUppyState({ inputName, files: {} });
    };
  }, []);

  const getMetaFields = () => {
    const dashboardMeta = [
      { id: 'name', name: i18n._('Name') },
    ];

    if (!hideType) {
      dashboardMeta.push({ id: 'category', name: i18n._('Type') });
    }
    if (!hideDescription) {
      dashboardMeta.push({ id: 'description', name: i18n._('Description') });
    }

    return dashboardMeta;
  };

  return (
    uppy &&
      <Dashboard
        id={inputName}
        uppy={uppy}
        hideUploadButton
        locale={locale}
        plugins={['ImageEditor', 'ScreenCapture', 'Webcam']}
        proudlyDisplayPoweredByUppy={false}
        metaFields={getMetaFields()}
      />
  );
};

export default FileUploadField;
